import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MessageCircle, ChevronRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { productsData } from '@/data/productsData';
import { StarRating } from '@/components/StarRating';

const featuredProducts = productsData.filter(p => p.popular).slice(0, 4);

export const FeaturedProducts = () => {
  return (
    <section className="py-24 relative overflow-hidden section-premium-light texture-grain">
      {/* Soft Radial Glow Effects */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[150px]" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gold/5 rounded-full blur-[120px]" />
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 text-primary font-semibold text-sm uppercase tracking-wider mb-4">
            <span className="w-8 h-px bg-primary/50" />
            OUR PRODUCTS
            <span className="w-8 h-px bg-primary/50" />
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
            Featured <span className="text-primary">Supplements</span>
          </h2>
          <p className="text-base text-muted-foreground max-w-2xl mx-auto">
            Premium quality, 100% authentic supplements for your fitness journey. All products verified and sourced from authorized distributors.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {featuredProducts.map((product, index) => (
            <Link key={product.id} to={`/products/${product.slug}`} data-testid={`featured-product-${product.id}`}>
              <Card
                className="group overflow-hidden bg-white/80 backdrop-blur-sm border border-border/50 hover:border-primary/30 card-hover animate-fade-up h-full"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Product Image */}
                <div className="relative h-56 bg-muted overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  
                  {product.popular && (
                    <Badge className="absolute top-4 left-4 bg-gold/90 text-white border-0">
                      Popular
                    </Badge>
                  )}
                  {/* Verified Badge */}
                  <div className="absolute top-4 right-4 w-8 h-8 rounded-full bg-green-500/90 backdrop-blur-sm flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-white" strokeWidth={1.5} />
                  </div>
                </div>

                <CardContent className="p-5">
                  {/* Brand */}
                  <span className="text-xs font-semibold text-primary uppercase tracking-wider">
                    {product.brand}
                  </span>

                  {/* Product Name */}
                  <h3 className="font-bold text-foreground text-lg mt-2 mb-1 line-clamp-2 group-hover:text-primary transition-colors">
                    {product.name}
                  </h3>

                  {/* Star Rating */}
                  <div className="mb-2">
                    <StarRating slug={product.slug} />
                  </div>

                  {/* Description */}
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {product.description}
                  </p>

                  {/* Features */}
                  <div className="flex flex-wrap gap-2">
                    {product.features.slice(0, 2).map((feature) => (
                      <span
                        key={feature}
                        className="text-xs px-2 py-1 bg-primary/5 border border-primary/10 rounded-full text-muted-foreground"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* View All Products Button */}
        <div className="text-center mt-12">
          <Button 
            size="lg" 
            className="bg-primary hover:bg-primary/90 text-white px-8 transition-all hover:-translate-y-0.5"
            asChild
          >
            <Link to="/products">
              View All Products
              <ChevronRight className="w-5 h-5 ml-2" strokeWidth={1.5} />
            </Link>
          </Button>
        </div>
      </div>

      {/* Section Separator */}
      <div className="absolute bottom-0 left-0 right-0 section-separator" />
    </section>
  );
};

export default FeaturedProducts;
